import React, { useEffect, useState } from 'react';
import { AlertTriangleIcon, HelpCircleIcon, PlusIcon, TrashIcon } from 'lucide-react';
interface RuleEditorProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}
type RuleTemplate = {
  key: string;
  label: string;
  type: 'number' | 'boolean' | 'string' | 'array' | 'percentage';
  description: string;
};
export function RuleEditor({
  value,
  onChange,
  error
}: RuleEditorProps) {
  const [jsonMode, setJsonMode] = useState(false);
  const [rules, setRules] = useState<Record<string, any>>({});
  const [jsonValue, setJsonValue] = useState('');
  const [jsonError, setJsonError] = useState('');
  // Common rule templates
  const ruleTemplates: RuleTemplate[] = [{
    key: 'maxPerTransaction',
    label: 'Max Per Transaction',
    type: 'number',
    description: 'Maximum amount allowed per transaction'
  }, {
    key: 'warningThreshold',
    label: 'Warning Threshold',
    type: 'percentage',
    description: 'Percentage threshold for budget warnings'
  }, {
    key: 'vendorBlacklist',
    label: 'Vendor Blacklist',
    type: 'array',
    description: 'List of vendors not allowed to use this budget'
  }, {
    key: 'approvalRequired',
    label: 'Approval Required',
    type: 'boolean',
    description: 'Whether transactions require explicit approval'
  }, {
    key: 'projectCode',
    label: 'Project Code',
    type: 'string',
    description: 'Associated project code'
  }, {
    key: 'campaignCode',
    label: 'Campaign Code',
    type: 'string',
    description: 'Associated marketing campaign code'
  }];
  // Initialize rules from JSON string
  useEffect(() => {
    try {
      const parsedRules = JSON.parse(value);
      setRules(parsedRules);
      setJsonValue(JSON.stringify(parsedRules, null, 2));
      setJsonError('');
    } catch (error) {
      setJsonValue(value);
      setJsonError('Invalid JSON format');
    }
  }, [value]);
  // Update JSON when rules change
  const updateRules = (newRules: Record<string, any>) => {
    setRules(newRules);
    const newJsonValue = JSON.stringify(newRules, null, 2);
    setJsonValue(newJsonValue);
    onChange(newJsonValue);
  };
  // Handle manual JSON editing
  const handleJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setJsonValue(newValue);
    try {
      const parsedRules = JSON.parse(newValue);
      setRules(parsedRules);
      setJsonError('');
      onChange(newValue);
    } catch (error) {
      setJsonError('Invalid JSON format');
      onChange(newValue);
    }
  };
  // Add a new rule
  const addRule = (template: RuleTemplate) => {
    const newRules = {
      ...rules
    };
    // Set default value based on type
    let defaultValue: any;
    switch (template.type) {
      case 'number':
        defaultValue = 0;
        break;
      case 'boolean':
        defaultValue = false;
        break;
      case 'string':
        defaultValue = '';
        break;
      case 'array':
        defaultValue = [];
        break;
      case 'percentage':
        defaultValue = 0.8; // Default to 80%
        break;
      default:
        defaultValue = null;
    }
    newRules[template.key] = defaultValue;
    updateRules(newRules);
  };
  // Update a rule value
  const updateRuleValue = (key: string, value: any) => {
    const newRules = {
      ...rules
    };
    newRules[key] = value;
    updateRules(newRules);
  };
  // Remove a rule
  const removeRule = (key: string) => {
    const newRules = {
      ...rules
    };
    delete newRules[key];
    updateRules(newRules);
  };
  // Render input based on rule type
  const renderRuleInput = (key: string, type: string, currentValue: any) => {
    switch (type) {
      case 'number':
        return <input type="number" value={currentValue} onChange={e => updateRuleValue(key, parseFloat(e.target.value) || 0)} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8869" />;
      case 'boolean':
        return <select value={currentValue ? 'true' : 'false'} onChange={e => updateRuleValue(key, e.target.value === 'true')} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8870">
            <option value="true" data-id="element-8871">Yes</option>
            <option value="false" data-id="element-8872">No</option>
          </select>;
      case 'string':
        return <input type="text" value={currentValue} onChange={e => updateRuleValue(key, e.target.value)} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8873" />;
      case 'array':
        return <div data-id="element-8874">
            {Array.isArray(currentValue) && currentValue.map((item, index) => <div key={index} className="flex items-center mb-2" data-id="element-8875">
                  <input type="text" value={item} onChange={e => {
              const newArray = [...currentValue];
              newArray[index] = e.target.value;
              updateRuleValue(key, newArray);
            }} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8876" />
                  <button type="button" onClick={() => {
              const newArray = [...currentValue];
              newArray.splice(index, 1);
              updateRuleValue(key, newArray);
            }} className="ml-2 text-red-600 hover:text-red-900" data-id="element-8877">
                    <TrashIcon className="h-4 w-4" data-id="element-8878" />
                  </button>
                </div>)}
            <button type="button" onClick={() => {
            const newArray = Array.isArray(currentValue) ? [...currentValue, ''] : [''];
            updateRuleValue(key, newArray);
          }} className="mt-1 inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8879">
              <PlusIcon className="h-3 w-3 mr-1" data-id="element-8880" />
              Add Item
            </button>
          </div>;
      case 'percentage':
        return <div className="relative rounded-md shadow-sm" data-id="element-8881">
            <input type="number" min="0" max="1" step="0.05" value={currentValue} onChange={e => updateRuleValue(key, parseFloat(e.target.value) || 0)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-12 sm:text-sm border-gray-300 rounded-md" data-id="element-8882" />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-8883">
              <span className="text-gray-500 sm:text-sm" data-id="element-8884">
                {(currentValue * 100).toFixed(0)}%
              </span>
            </div>
          </div>;
      default:
        return <input type="text" value={currentValue} onChange={e => updateRuleValue(key, e.target.value)} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8885" />;
    }
  };
  // Determine the type of an existing rule
  const determineRuleType = (key: string, value: any): string => {
    const template = ruleTemplates.find(t => t.key === key);
    if (template) {
      return template.type;
    }
    if (typeof value === 'number') {
      if (key.toLowerCase().includes('threshold') && value >= 0 && value <= 1) {
        return 'percentage';
      }
      return 'number';
    }
    if (typeof value === 'boolean') {
      return 'boolean';
    }
    if (Array.isArray(value)) {
      return 'array';
    }
    return 'string';
  };
  return <div className="space-y-4" data-id="element-8886">
      <div className="flex justify-between items-center" data-id="element-8887">
        <div className="flex items-center" data-id="element-8888">
          <h5 className="text-sm font-medium text-gray-900" data-id="element-8889">Budget Rules</h5>
          <div className="ml-2 text-gray-400 hover:text-gray-500" data-id="element-8890">
            <HelpCircleIcon className="h-4 w-4" title="Rules define constraints on budget usage" data-id="element-8891" />
          </div>
        </div>
        <div data-id="element-8892">
          <button type="button" onClick={() => setJsonMode(!jsonMode)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8893">
            {jsonMode ? 'Switch to Form Mode' : 'Switch to JSON Mode'}
          </button>
        </div>
      </div>
      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md" data-id="element-8894">
          <div className="flex items-center" data-id="element-8895">
            <AlertTriangleIcon className="h-5 w-5 text-red-400 mr-2" data-id="element-8896" />
            <span className="text-sm text-red-700" data-id="element-8897">{error}</span>
          </div>
        </div>}
      {jsonMode ? <div data-id="element-8898">
          <textarea value={jsonValue} onChange={handleJsonChange} rows={10} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md font-mono" data-id="element-8899" />
          {jsonError && <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md" data-id="element-8900">
              <div className="flex items-center" data-id="element-8901">
                <AlertTriangleIcon className="h-4 w-4 text-red-400 mr-2" data-id="element-8902" />
                <span className="text-sm text-red-700" data-id="element-8903">{jsonError}</span>
              </div>
            </div>}
        </div> : <div className="space-y-4" data-id="element-8904">
          {/* Existing Rules */}
          {Object.keys(rules).length > 0 ? <div className="space-y-4" data-id="element-8905">
              {Object.entries(rules).map(([key, value]) => {
          const ruleType = determineRuleType(key, value);
          const template = ruleTemplates.find(t => t.key === key);
          return <div key={key} className="border border-gray-200 rounded-md p-4" data-id="element-8906">
                    <div className="flex justify-between items-start mb-2" data-id="element-8907">
                      <div data-id="element-8908">
                        <h6 className="text-sm font-medium text-gray-900" data-id="element-8909">
                          {template?.label || key}
                        </h6>
                        <p className="text-xs text-gray-500" data-id="element-8910">
                          {template?.description || `Custom rule: ${key}`}
                        </p>
                      </div>
                      <button type="button" onClick={() => removeRule(key)} className="text-red-600 hover:text-red-900" data-id="element-8911">
                        <TrashIcon className="h-4 w-4" data-id="element-8912" />
                      </button>
                    </div>
                    {renderRuleInput(key, ruleType, value)}
                  </div>;
        })}
            </div> : <div className="text-center py-4 text-sm text-gray-500 border border-dashed border-gray-300 rounded-md" data-id="element-8913">
              No rules defined. Add a rule from the templates below.
            </div>}
          {/* Add Rule Templates */}
          <div className="mt-4" data-id="element-8914">
            <h6 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3" data-id="element-8915">
              Add Rule from Template
            </h6>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-8916">
              {ruleTemplates.filter(template => !(template.key in rules)).map(template => <button key={template.key} type="button" onClick={() => addRule(template)} className="text-left p-2 border border-gray-200 rounded-md hover:bg-gray-50" data-id="element-8917">
                    <div className="text-sm font-medium text-gray-900" data-id="element-8918">
                      {template.label}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-8919">
                      {template.description}
                    </div>
                  </button>)}
            </div>
          </div>
        </div>}
    </div>;
}